import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Progress - Progress bar indicator
 * 
 * Shows task completion or loading progress
 * - Rozmiary: "1" | "2" | "3"
 * - Colors: "pink" | "gray" | "success" | "danger" | "warning"
 * - Smooth animations
 * 
 * @example
 * ```tsx
 * <Progress value={60} max={100} />
 * <Progress value={75} color="success" size="3" />
 * ```
 */

const progressVariants = cva(
  cn(
    "relative overflow-hidden rounded-full",
    "bg-[var(--dark-a4)]"
  ),
  {
    variants: {
      size: {
        "1": "h-[6px]",
        "2": "h-[8px]",
        "3": "h-[12px]",
      },
    },
    defaultVariants: {
      size: "2",
    },
  }
)

const progressIndicatorVariants = cva(
  cn(
    "h-full w-full flex-1",
    "transition-all duration-300 ease-out"
  ),
  {
    variants: {
      color: {
        pink: "bg-[var(--pink-9)]",
        gray: "bg-[var(--gray-9)]",
        success: "bg-[var(--success-9)]",
        danger: "bg-[var(--danger-9)]",
        warning: "bg-[var(--warning-9)]",
      },
    },
    defaultVariants: {
      color: "pink",
    },
  }
)

export interface ProgressProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressVariants>,
    VariantProps<typeof progressIndicatorVariants> {
  /**
   * Current value
   */
  value?: number
  /**
   * Maximum value
   * @default 100
   */
  max?: number
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, size, color, value = 0, max = 100, ...props }, ref) => {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100))

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label="Progress"
        className={cn(progressVariants({ size }), className)}
        {...props}
      >
        <div
          className={cn(progressIndicatorVariants({ color }))}
          style={{ transform: `translateX(-${100 - percentage}%)` }}
        />
      </div>
    )
  }
)
Progress.displayName = "Progress"

export { Progress, progressVariants }

